import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  SlidersHorizontal,
  X,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";
import FloatingActions from "../components/user/FloatingActions";
import useUserProducts from "../hooks/user/useUserProduct";
import useHomepageData from "../hooks/user/useHomepageData";
import useDebounce from "../hooks/useDebounce";
import { addToCart } from "../utils/CartUtils";
import { useNotification } from "../context/NotificationContext";
import { formatPrice } from "../utils/productUtils";

function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333]">
      <div className="h-64 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] animate-shimmer"></div>
      <div className="p-6 space-y-3">
        <div className="h-3 w-20 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
        <div className="h-4 w-32 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
        <div className="h-3 w-24 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
        <div className="h-6 w-28 bg-gradient-to-r from-[#F5F5F5] via-[#E0E0E0] to-[#F5F5F5] dark:from-[#2A2A2A] dark:via-[#333333] dark:to-[#2A2A2A] rounded animate-shimmer"></div>
      </div>
      <style jsx="true">{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default function RentalsPage() {
  // --- Local State ---
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [showFiltersOnMobile, setShowFiltersOnMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("popular");

  // Sync category from URL if present
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setCurrentPage(1);
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  // Debounce search query to prevent excessive API calls
  const debouncedSearch = useDebounce(searchQuery, 500);
  const debouncedPriceMax = useDebounce(priceRange[1], 500);

  // --- Data Fetching ---
  const { categories: rawCategories, loading: categoriesLoading } = useHomepageData();


  // Prepare filters for the hook
  const filters = useMemo(() => ({
    page: currentPage,
    limit: 20,
    search: debouncedSearch,
    category: selectedCategory === "All Categories" ? "" : selectedCategory,
    sort: sortBy,
  }), [currentPage, debouncedSearch, selectedCategory, sortBy]);

  const { products, meta, loading: productsLoading, error } = useUserProducts(filters);
  const { showNotification } = useNotification();
  const rentalsSectionRef = useRef(null);

  // --- Helpers ---
  const handleAddToCart = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const result = addToCart(item, 1);

    showNotification({
      type: result.success ? "success" : "failed",
      title: result.success ? "Added to Cart" : "Action Failed",
      message: result.message
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    rentalsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("All Categories");
    setPriceRange([0, 100000]);
    setSortBy("popular");
    setCurrentPage(1);
  };

  const activeFiltersCount =
    (searchQuery ? 1 : 0) +
    (selectedCategory !== "All Categories" ? 1 : 0) +
    (priceRange[0] !== 0 || priceRange[1] !== 100000 ? 1 : 0);

  // Transform raw categories to simple list if needed, or use directly
  // HomepageService returns objects: { id, name, ... }
  // We need to handle "All Categories" + mapped names
  const categoryNames = useMemo(() => {
    const names = rawCategories.map(c => c.name);
    return ["All Categories", ...names];
  }, [rawCategories]);

  return (
    <>
      <Header />
      <div
        className="min-h-screen bg-white dark:bg-[#121212]"
      >
        <section className="relative h-[300px] md:h-[400px] overflow-hidden bg-gradient-to-br from-[#62397f] via-[#7C3AED] to-[#6D28D9]">
          <div className="absolute inset-0 opacity-10 bg-cover bg-center" style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1511578314322-379afb476865?w=2000&h=800&fit=crop")' }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
          <div className="relative h-full max-w-[1400px] mx-auto px-6 flex items-center">
            <div className="w-full">
              <h1
                className="text-white font-medium font-serif leading-tight mb-4"
                style={{
                  fontSize: "clamp(36px, 6vw, 44px)",
                }}
              >
                St. Stephen's Premium Rentals
              </h1>
              <p className="text-white/90 text-lg md:text-xl mb-8 max-w-2xl">
                Browse our extensive collection of event rentals for your
                perfect celebration
              </p>
              <div className="max-w-2xl">
                <div className="relative">
                  <Search
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666666]"
                    size={20}
                  />
                  <input
                    type="text"
                    placeholder="Search rentals by name or category..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1); // Reset to page 1 on search
                    }}
                    className="w-full pl-12 pr-4 py-4 rounded-full bg-white dark:bg-[#1E1E1E] text-black dark:text-white border-2 border-transparent focus:border-white focus:outline-none transition-all duration-200 shadow-[0_8px_24px_rgba(0,0,0,0.15)]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-[1400px] mx-auto px-6 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-[#FAFAFA] dark:bg-[#1E1E1E] rounded-2xl p-6 border border-[#E9E9E9] dark:border-[#333333]">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-black dark:text-white">
                      Filters
                    </h3>
                    {activeFiltersCount > 0 && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-[#62397f] dark:text-[#A78BFA] hover:underline"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  {/* Category Filter */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                      Category
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {categoriesLoading ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4].map(i => <div key={i} className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />)}
                        </div>
                      ) : (
                        categoryNames.map((category) => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setCurrentPage(1);
                            }}
                            className={`font-serif w-full text-left px-3 py-2 rounded-lg text-[15px] transition-colors duration-150 ${selectedCategory === category ? "bg-[#62397f] text-white font-bold" : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440] font-medium"}`}
                          >
                            {category}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Price Filter */}
                  {/* <div className="mb-6">
                    <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                      Price Range
                    </h4>
                    <div className="space-y-3">
                      <input
                        type="range"
                        min="0"
                        max="100000"
                        step="1000"
                        value={priceRange[1]}
                        onChange={(e) => {
                          setPriceRange([0, parseInt(e.target.value)]);
                        }}
                        className="w-full accent-[#62397f]"
                      />
                       <div className="flex items-center justify-between text-sm text-[#666666] dark:text-[#A0A0A0]">
                        <span>₦0</span>
                        <span>₦{priceRange[1].toLocaleString()}</span>
                      </div>
                      
                    </div>
                  </div> */}

                  {/* Sort Filter */}
                  <div>
                    <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                      Sort By
                    </h4>
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-white dark:bg-[#2A2A2A] text-black dark:text-white border border-[#E9E9E9] dark:border-[#333333] text-sm focus:outline-none focus:border-[#62397f]"
                    >
                      <option value="popular">Most Popular</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="availability">Most Available</option>
                    </select>
                  </div>
                </div>
              </div>
            </aside>

            {/* Mobile Filter Toggle */}
            <div className="lg:hidden flex items-center justify-between mb-4">
              <p className="text-sm text-[#666666] dark:text-[#A0A0A0]">
                {meta?.total || 0} items found
              </p>
              <button
                onClick={() => setShowFiltersOnMobile(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] rounded-full font-medium text-sm"
              >
                <SlidersHorizontal size={18} />
                Filters
                {activeFiltersCount > 0 && (
                  <span className="bg-[#62397f] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Filter Modal */}
            {showFiltersOnMobile && (
              <div className="lg:hidden fixed inset-0 z-50">
                <div
                  className="absolute inset-0 bg-black/50"
                  onClick={() => setShowFiltersOnMobile(false)}
                ></div>
                <div className="absolute right-0 top-0 bottom-0 w-[320px] bg-white dark:bg-[#1E1E1E] shadow-2xl overflow-y-auto">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-bold text-black dark:text-white">
                        Filters
                      </h3>
                      <button onClick={() => setShowFiltersOnMobile(false)}>
                        <X
                          size={24}
                          className="text-[#666666] dark:text-[#A0A0A0]"
                        />
                      </button>
                    </div>
                    {/* Reuse filters logic here - duplicated for mobile view as per original design */}
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                        Category
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {categoryNames.map((category) => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setCurrentPage(1);
                            }}
                            className={`font-serif w-full text-left px-3 py-2 rounded-lg text-[15px] transition-colors duration-150 ${selectedCategory === category ? "bg-[#62397f] text-white font-bold" : "text-[#666666] dark:text-[#A0A0A0] hover:bg-[#F5F3FF] dark:hover:bg-[#2A2440] font-medium"}`}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* <div className="mb-6">
                      <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                        Price Range
                      </h4>
                      <div className="space-y-3">
                        <input
                          type="range"
                          min="0"
                          max="100000"
                          step="1000"
                          value={priceRange[1]}
                          onChange={(e) => {
                            setPriceRange([0, parseInt(e.target.value)]);
                          }}
                          className="w-full accent-[#62397f]"
                        />
                        <div className="flex items-center justify-between text-sm text-[#666666] dark:text-[#A0A0A0]">
                          <span>₦0</span>
                          <span>₦{priceRange[1].toLocaleString()}</span>
                        </div>
                      </div>
                    </div> */}
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-black dark:text-white mb-3">
                        Sort By
                      </h4>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-white dark:bg-[#2A2A2A] text-black dark:text-white border border-[#E9E9E9] dark:border-[#333333] text-sm focus:outline-none focus:border-[#62397f]"
                      >
                        <option value="popular">Most Popular</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                        <option value="availability">Most Available</option>
                      </select>
                    </div>

                    <button
                      onClick={() => setShowFiltersOnMobile(false)}
                      className="w-full px-6 py-3 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="flex-1">
              {/* Active Filters Display */}
              {activeFiltersCount > 0 && (
                <div className="mb-6 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-[#666666] dark:text-[#A0A0A0]">
                    Active Filters:
                  </span>
                  {searchQuery && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] rounded-full text-sm">
                      Search: "{searchQuery}"
                      <button onClick={() => setSearchQuery("")}>
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {selectedCategory !== "All Categories" && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] rounded-full text-sm">
                      {selectedCategory}
                      <button
                        onClick={() => setSelectedCategory("All Categories")}
                      >
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {(priceRange[0] !== 0 || priceRange[1] !== 100000) && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] dark:text-[#A78BFA] rounded-full text-sm">
                      Up to ₦{priceRange[1].toLocaleString()}
                      <button onClick={() => setPriceRange([0, 100000])}>
                        <X size={14} />
                      </button>
                    </span>
                  )}
                </div>
              )}

              <div className="hidden lg:block mb-6">
                <p className="text-sm text-[#666666] dark:text-[#A0A0A0]">
                  {productsLoading
                    ? "Loading..."
                    : `Showing ${products.length} of ${meta?.total || 0} items`}
                </p>
              </div>

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
                  <strong className="font-bold">Error!</strong>
                  <span className="block sm:inline"> {error.message || "Failed to load products. Please try again."}</span>
                </div>
              )}

              {/* Product Grid */}
              {productsLoading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <SkeletonCard key={index} />
                  ))}
                </div>
              ) : products.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
                  {products.map((item) => (
                    <Link
                      key={item.id}
                      to={`/rental/${item.slug}`} // Updated to use react-router Link
                      className="group bg-white dark:bg-[#1E1E1E] rounded-2xl overflow-hidden border border-[#E9E9E9] dark:border-[#333333] transition-all duration-300 hover:shadow-[0_12px_32px_rgba(139,92,246,0.12)] dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.3)] transform hover:-translate-y-2"
                    >
                      <div className="relative h-64 overflow-hidden bg-[#F5F5F5] dark:bg-[#2A2A2A]">
                        <img
                          src={item.image || "https://placehold.co/600x400?text=No+Image"} // Fallback image
                          alt={item.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {item.popular && (
                          <div className="absolute top-4 left-4 px-3 py-1 bg-[#62397f] text-white text-xs font-bold uppercase tracking-wider rounded-full">
                            Popular
                          </div>
                        )}
                        <button
                          onClick={(e) => handleAddToCart(e, item)}
                          className="absolute bottom-4 right-4 w-12 h-12 bg-white dark:bg-[#2A2A2A] rounded-full flex items-center justify-center shadow-lg transform translate-y-16 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#62397f] hover:text-white text-[#62397f] dark:text-[#A78BFA]"
                          aria-label="Add to cart"
                        >
                          <ShoppingCart size={20} />
                        </button>
                      </div>
                      <div className="p-6">
                        <p className="font-serif text-[13px] font-bold text-[#62397f] dark:text-[#A78BFA] uppercase tracking-wider mb-2">
                          {item.category}
                        </p>
                        <h3 className="font-serif text-sm font-bold text-black dark:text-white mb-2 group-hover:text-[#62397f] dark:group-hover:text-[#A78BFA] transition-colors duration-200">
                          {item.name}
                        </h3>
                        {item.qtyAvailable !== undefined && (
                          <p className="text-sm text-[#999999] dark:text-[#666666] mb-3">
                            {item.qtyAvailable} available
                          </p>
                        )}
                        <div className="flex flex-col lg:flex-row lg:items-baseline gap-1">
                          <span className="text-[14px] md:text-[16px] lg:text-[24px] font-bold text-[#62397f] dark:text-[#A78BFA]">
                            ₦{formatPrice(item.price)}
                          </span>
                          <span className="text-xs md:text-sm lg:text-sm text-[#999999] dark:text-[#666666] lg:ml-1">
                            {item.unit}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="mb-6">
                    <div className="w-32 h-32 mx-auto bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center">
                      <Search
                        size={48}
                        className="text-[#62397f] dark:text-[#A78BFA]"
                      />
                    </div>
                  </div>
                  <h3
                    className="text-black dark:text-white font-bold font-serif mb-3"
                    style={{
                      fontSize: "clamp(24px, 4vw, 32px)",
                    }}
                  >
                    No rentals found
                  </h3>
                  <p className="text-[#666666] dark:text-[#A0A0A0] mb-8 max-w-md mx-auto">
                    We couldn't find any items matching your search criteria.
                    Try adjusting your filters or search terms.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-[#62397f] hover:bg-[#7C3AED] text-white font-semibold rounded-full transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}

              {/* Pagination */}
              {!productsLoading && meta && meta.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-[#1E1E1E] border border-[#E9E9E9] dark:border-[#333333] text-[#666666] dark:text-[#A0A0A0] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#62397f] dark:hover:border-[#A78BFA] transition-colors duration-150"
                  >
                    <ChevronLeft size={18} />
                    <span className="hidden sm:inline">Previous</span>
                  </button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(meta.totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (meta.totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= meta.totalPages - 2)
                        pageNum = meta.totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      // Ensure pageNum is within bounds
                      if (pageNum < 1) pageNum = i + 1;
                      if (pageNum > meta.totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-full font-semibold transition-all duration-200 ${currentPage === pageNum ? "bg-[#62397f] text-white" : "bg-white dark:bg-[#1E1E1E] border border-[#E9E9E9] dark:border-[#333333] text-[#666666] dark:text-[#A0A0A0] hover:border-[#62397f] dark:hover:border-[#A78BFA]"}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === meta.totalPages}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-[#1E1E1E] border border-[#E9E9E9] dark:border-[#333333] text-[#666666] dark:text-[#A0A0A0] disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#62397f] dark:hover:border-[#A78BFA] transition-colors duration-150"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <FloatingActions />
    </>
  );
}
